import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import CustomModal from "../CustomModal";
import Row from "../../../components/Row";
import Column from "../../../components/Column";
import Text from "../../../components/Text";
import styled from "styled-components";
import { styles } from "../../../styles/themes/style";
import { useConnectionsStore } from "../../../stores/connections/useConnectionsStore";
import { useTagsHandlers } from "../../../services/actions/tags/useTagsHandlers";
import { useTagsStore } from "../../../stores/tags/useTagsStore";
import TabsView, { ITabProperties } from "../../../components/TabsView";
import Checkbox from "../../../components/Checkbox";
import AppTag from "../../../fragments/AppTag";
import ColorSelectDropdownFragment from "../../../fragments/ColorSelectDropdownFragment";
import PrivateCheckBoxControl from "../../../fragments/PrivateCheckBoxControl";
import { useConnectionsHandlers } from "../../../services/actions/connections/useConnectionsHandlers";
import Button from "../../Button";
import TooltipWrapper from "../../TooltipWrapper";
import { useAuthStore } from "../../../stores/auth/useAuthStore";
import { useInboxStore } from "../../../stores/inbox/useInboxStore";

interface IBulkEditTagsModalProps {
  selectedConnectionsIds?: string[];
  selectedAllConnections?: boolean;
  /** When opening from Inbox, connection rows may be missing from the connections store — pass tags per connectionId for correct initial checkboxes. */
  connectionTagSnapshots?: Array<{ connectionId: string; tags?: any[] }>;
  /** Inbox-only: allow closing by clicking the backdrop. */
  closeOnBackdropClick?: boolean;
}

/** AppModal reads this so backdrop close respects in-flight bulk tag API (same as X). */
let bulkEditTagsBlockBackdropClose = false;
export const isBulkEditTagsBackdropCloseBlocked = () => bulkEditTagsBlockBackdropClose;

/** Normalize tag _id for Set / Map (Mongo strings vs objects). */
const tagIdKey = (tag: any) => String(tag?._id ?? "");
const EMPTY_SELECTED_CONNECTION_IDS: string[] = [];
const EMPTY_CONNECTION_TAG_SNAPSHOTS: Array<{ connectionId: string; tags?: any[] }> = [];

const areSetsEqual = (a: Set<string>, b: Set<string>) => {
  if (a.size !== b.size) return false;
  return Array.from(a).every((value) => b.has(value));
};

const BulkEditTagsModal: FunctionComponent<IBulkEditTagsModalProps> = ({
  selectedConnectionsIds = EMPTY_SELECTED_CONNECTION_IDS,
  selectedAllConnections = false,
  connectionTagSnapshots = EMPTY_CONNECTION_TAG_SNAPSHOTS,
  closeOnBackdropClick = false,
}) => {
  const {
    store: { connections, connectionFilterOptions },
  } = useConnectionsStore();
  const { bulkApplyTags, bulkRemoveTags } = useConnectionsHandlers();
  const { dispatch: inboxDispatch } = useInboxStore();
  const { createTagHandler, loadAllTagsHandler } = useTagsHandlers();
  const {
    store: { allTags: allTagsStore },
  } = useTagsStore();
  const {
    store: { workspace, user, workspaceOwner },
  } = useAuthStore();

  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<"all" | "mutual" | "individual">(
    "all"
  );
  const [newTagColor, setNewTagColor] = useState(styles.colors.primary600);
  const [newTagPrivate, setNewTagPrivate] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  // kept for potential future loading state
  const [localAppliedIds, setLocalAppliedIds] = useState<Set<string>>(
    new Set()
  );
  // Tags present on some but not all selected connections (indeterminate checkbox)
  const [localPartialIds, setLocalPartialIds] = useState<Set<string>>(
    new Set()
  );

  // Buffer for actions - separate apply and remove actions
  const [pendingApplyTags, setPendingApplyTags] = useState<Set<string>>(
    new Set()
  );
  const [pendingRemoveTags, setPendingRemoveTags] = useState<Set<string>>(
    new Set()
  );
  const pendingApplyTagsRef = useRef(pendingApplyTags);
  const pendingRemoveTagsRef = useRef(pendingRemoveTags);

  // Queue system for API calls
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingChangesQueue, setPendingChangesQueue] = useState<
    Array<{
      tagsToApply: string[];
      tagsToRemove: string[];
    }>
  >([]);

  // Lock checkboxes from first click until that tag's batch finishes
  const [busyTagIds, setBusyTagIds] = useState<Set<string>>(new Set());
  const busyTagIdsRef = useRef<Set<string>>(new Set());

  const markTagsBusy = useCallback((ids: string[]) => {
    ids.forEach((id) => busyTagIdsRef.current.add(String(id)));
    setBusyTagIds(new Set(busyTagIdsRef.current));
  }, []);

  const clearTagsBusy = useCallback((ids: string[]) => {
    ids.forEach((id) => busyTagIdsRef.current.delete(String(id)));
    setBusyTagIds(new Set(busyTagIdsRef.current));
  }, []);

  // Keyboard navigation state
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const createButtonRef = useRef<HTMLDivElement>(null);
  const tagItemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const batchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /** After bulk apply/remove, server returns fresh `tags` per connection — overrides stale inbox snapshots. */
  const [bulkSyncedTagsByConnectionId, setBulkSyncedTagsByConnectionId] = useState<
    Record<string, any[]>
  >({});
  const bulkSyncedTagsByConnectionIdRef = useRef<Record<string, any[]>>({});

  useEffect(() => {
    bulkSyncedTagsByConnectionIdRef.current = bulkSyncedTagsByConnectionId;
  }, [bulkSyncedTagsByConnectionId]);

  const executeBatchChanges = useCallback(
    async (tagsToApply: string[], tagsToRemove: string[]) => {
      if (tagsToApply.length === 0 && tagsToRemove.length === 0) return;

      try {
        // Execute both operations in parallel and detect failures
        const promises: Promise<any>[] = [];

        if (tagsToApply.length > 0) {
          promises.push(
            bulkApplyTags(
              tagsToApply,
              selectedAllConnections ? [] : selectedConnectionsIds || []
            )
          );
        }

        if (tagsToRemove.length > 0) {
          promises.push(
            bulkRemoveTags(
              tagsToRemove,
              selectedAllConnections ? [] : selectedConnectionsIds || []
            )
          );
        }

        // Wait for both operations to complete
        const results = await Promise.all(promises);

        // Handlers return undefined on failure; treat that as an error to trigger rollback + refetch
        const anyFailed = results.some((r) => !r || (r?.success === false));
        if (anyFailed) {
          throw new Error("Bulk tag update failed");
        }

        /**
         * IMPORTANT: do NOT trust the API-returned `connection.tags` to reflect the just-applied changes.
         * We already saw endpoints returning stale tags (tag "snap-back" bugs). Instead, compute the next
         * tag list locally from current selectedConnections + the apply/remove delta, then sync both:
         *  - `bulkSyncedTagsByConnectionId` (modal state)
         *  - inbox store (list + sidebar + bulk selection)
         */
        const tagsUniverse: any[] = [
          ...(allTagsStore || []),
          ...(connectionFilterOptions?.tagFilterOptions || []),
        ];
        const tagById = new Map<string, any>(
          tagsUniverse
            .filter((t: any) => t?._id != null)
            .map((t: any) => [String(t._id), t])
        );
        const ensureTagObj = (id: string) =>
          tagById.get(String(id)) || { _id: String(id), title: "", color: styles.colors.primary600 };

        const selectedIds = (selectedAllConnections ? [] : (selectedConnectionsIds || [])).map(String);
        const nextByConnectionId: Record<string, any[]> = {};

        // Build base tags per connectionId similarly to `selectedConnections` useMemo,
        // but using refs so we always use latest modal overrides.
        const storeById = new Map<string, any>();
        (connections || []).forEach((c: any) => {
          if (c?._id != null) storeById.set(String(c._id), c);
        });
        const snapById = new Map<string, any[]>();
        (connectionTagSnapshots || []).forEach((s) => {
          if (s?.connectionId != null) snapById.set(String(s.connectionId), s.tags || []);
        });
        const overrides = bulkSyncedTagsByConnectionIdRef.current || {};

        const idsToProcess = selectedAllConnections ? Object.keys(overrides) : selectedIds;
        idsToProcess.forEach((cid) => {
          const key = String(cid);
          const fromStore = storeById.get(key);
          const baseTags: any[] =
            overrides[key] !== undefined
              ? overrides[key]
              : fromStore?.tags
              ? fromStore.tags
              : snapById.get(key) || [];
          const baseById = new Map<string, any>(
            (Array.isArray(baseTags) ? baseTags : [])
              .filter(Boolean)
              .map((t: any) => [String(t?._id), t])
          );
          tagsToApply.forEach((id) => baseById.set(String(id), ensureTagObj(String(id))));
          tagsToRemove.forEach((id) => baseById.delete(String(id)));
          nextByConnectionId[key] = Array.from(baseById.values());
        });

        if (Object.keys(nextByConnectionId).length > 0) {
          setBulkSyncedTagsByConnectionId((prev) => ({ ...prev, ...nextByConnectionId }));
          inboxDispatch({ type: "bulk-update-tags-by-connection", payload: nextByConnectionId });
        }

        setLocalAppliedIds((prev) => {
          const next = new Set<string>();
          prev.forEach((id) => next.add(String(id)));
          tagsToApply.forEach((id) => next.add(String(id)));
          tagsToRemove.forEach((id) => next.delete(String(id)));
          return next;
        });
        setLocalPartialIds((prev) => {
          const next = new Set(Array.from(prev).map(String));
          tagsToApply.forEach((id) => next.delete(String(id)));
          tagsToRemove.forEach((id) => next.delete(String(id)));
          return next;
        });

        // DO NOT refresh UI here - it will be done only when ALL API calls complete
      } catch (error) {
        console.error("Error executing batch changes:", error);

        // If this batch involved any Pipeline tags, ask Kanban to refetch (only on failure)
        const tagsUniverse: any[] = [
          ...(allTagsStore || []),
          ...(connectionFilterOptions?.tagFilterOptions || []),
        ];
        const tagIdToTag = new Map<string, any>(
          tagsUniverse.map((t: any) => [String(t?._id), t])
        );
        const affectedKanbanIds = Array.from(
          new Set(
            [...tagsToApply, ...tagsToRemove]
              .map((id) => tagIdToTag.get(String(id)))
              .filter((t: any) => t?.forKanban === true && t?.kanban)
              .map((t: any) => String(t.kanban))
          )
        );
        if (affectedKanbanIds.length > 0) {
          window.dispatchEvent(
            new CustomEvent("ld:kanban-refetch", {
              detail: { kanbanIds: affectedKanbanIds },
            })
          );
        }

        // Rollback changes on error
        setLocalAppliedIds((prev) => {
          const next = new Set(Array.from(prev).map(String));
          tagsToApply.forEach((tagId) => next.delete(String(tagId)));
          tagsToRemove.forEach((tagId) => next.add(String(tagId)));
          return next;
        });
      } finally {
        clearTagsBusy([...tagsToApply, ...tagsToRemove]);
      }
    },
    [
      bulkApplyTags,
      bulkRemoveTags,
      selectedAllConnections,
      selectedConnectionsIds,
      allTagsStore,
      connectionFilterOptions?.tagFilterOptions,
      inboxDispatch,
      connections,
      connectionTagSnapshots,
      clearTagsBusy,
    ]
  );
  const executeBatchChangesRef = useRef(executeBatchChanges);

  useEffect(() => {
    pendingApplyTagsRef.current = pendingApplyTags;
  }, [pendingApplyTags]);

  useEffect(() => {
    pendingRemoveTagsRef.current = pendingRemoveTags;
  }, [pendingRemoveTags]);

  useEffect(() => {
    executeBatchChangesRef.current = executeBatchChanges;
  }, [executeBatchChanges]);

  const scheduleBatchExecution = useCallback(() => {
    if (batchTimeoutRef.current) {
      clearTimeout(batchTimeoutRef.current);
    }

    batchTimeoutRef.current = setTimeout(() => {
      // Get current state of pending tags
      setPendingApplyTags((currentApplyTags) => {
        setPendingRemoveTags((currentRemoveTags) => {
          const tagsToApply = Array.from(currentApplyTags);
          const tagsToRemove = Array.from(currentRemoveTags);

          if (tagsToApply.length > 0 || tagsToRemove.length > 0) {
            // Add to queue instead of direct execution
            setPendingChangesQueue((prevQueue) => [
              ...prevQueue,
              { tagsToApply, tagsToRemove },
            ]);
          }

          return new Set(); // Reset pending remove tags
        });
        return new Set(); // Reset pending apply tags
      });

      batchTimeoutRef.current = null;
    }, 500);
  }, []);

  const computeUnion = (conns: any[]): Set<string> => {
    const union = new Set<string>();
    conns?.forEach((c: any) => {
      (c.tags || [])
        .filter((t: any) => !t.isForbidden)
        .forEach((t: any) => union.add(String(t._id)));
    });
    return union;
  };

  const computeIntersection = (conns: any[]): Set<string> => {
    if (!conns?.length) return new Set();
    let intersection: Set<string> | null = null;
    conns.forEach((c: any) => {
      const ids = new Set<string>(
        (c.tags || [])
          .filter((t: any) => !t.isForbidden)
          .map((t: any) => String(t._id))
      );
      if (intersection === null) {
        intersection = ids;
      } else {
        intersection = new Set(
          Array.from(intersection).filter((id) => ids.has(id))
        );
      }
    });
    return intersection || new Set();
  };

  const selectedConnections = useMemo(() => {
    if (!selectedConnectionsIds?.length) return [];
    const map = new Map<string, any>();
    connections.forEach((c: any) => {
      if (c?._id != null) map.set(String(c._id), c);
    });
    const snapById = new Map<string, any[]>();
    (connectionTagSnapshots || []).forEach((s) => {
      if (s?.connectionId != null)
        snapById.set(String(s.connectionId), s.tags || []);
    });

    return selectedConnectionsIds
      .map((id) => {
        const key = String(id);
        const fromStore = map.get(key);
        const bulkTags = bulkSyncedTagsByConnectionId[key];
        if (bulkTags !== undefined) {
          const base = fromStore || { _id: id };
          return { ...base, tags: bulkTags };
        }
        if (fromStore) return fromStore;
        if (snapById.has(key)) {
          return { _id: id, tags: snapById.get(key) || [] };
        }
        return null;
      })
      .filter(Boolean);
  }, [
    connections,
    selectedConnectionsIds,
    connectionTagSnapshots,
    bulkSyncedTagsByConnectionId,
  ]);

  const selectedConnectionsIdsKey = useMemo(
    () => selectedConnectionsIds.map(String).join(","),
    [selectedConnectionsIds]
  );

  const connectionTagSnapshotsKey = useMemo(
    () =>
      connectionTagSnapshots
        .map(
          (s) =>
            `${s.connectionId}:${(s.tags || [])
              .map((t) => tagIdKey(t))
              .sort()
              .join(",")}`
        )
        .join("|"),
    [connectionTagSnapshots]
  );

  useEffect(() => {
    if (!allTagsStore?.length) loadAllTagsHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Checkbox sync: fully applied = intersection, partial = union − intersection.
    // Skip while bulk ops are in flight so optimistic state is not overwritten.
    if (isProcessing || pendingChangesQueue.length > 0 || pendingApplyTags.size > 0 || pendingRemoveTags.size > 0) {
      return;
    }

    if (
      selectedConnectionsIds.length > 0 &&
      selectedConnections.length !== selectedConnectionsIds.length
    ) {
      return;
    }

    const union = computeUnion(selectedConnections);
    const intersection = computeIntersection(selectedConnections);
    const partial = new Set(
      Array.from(union).filter((id) => !intersection.has(id))
    );

    setLocalAppliedIds((prev) => {
      const next = new Set<string>(Array.from(intersection).map(String));
      return areSetsEqual(prev, next) ? prev : next;
    });
    setLocalPartialIds((prev) => {
      const next = new Set<string>(Array.from(partial).map(String));
      return areSetsEqual(prev, next) ? prev : next;
    });
  }, [
    selectedConnectionsIdsKey,
    connectionTagSnapshotsKey,
    selectedConnections,
    selectedConnectionsIds.length,
    isProcessing,
    pendingChangesQueue.length,
    pendingApplyTags.size,
    pendingRemoveTags.size,
  ]);

  useEffect(() => {
    // Seed per-connection tag overrides once, using the fully-resolved selectedConnections baseline.
    // This prevents "partial base" issues where some tags appear in the list but are missing in the modal state,
    // which then causes random checked/unchecked mismatches on reopen.
    if (!selectedConnectionsIds?.length) return;
    if (isProcessing || pendingChangesQueue.length > 0 || pendingApplyTags.size > 0 || pendingRemoveTags.size > 0) return;
    if (selectedConnections.length !== selectedConnectionsIds.length) return;

    setBulkSyncedTagsByConnectionId((prev) => {
      if (prev && Object.keys(prev).length > 0) return prev;
      const seeded: Record<string, any[]> = {};
      selectedConnections.forEach((c: any) => {
        const cid = String(c?._id ?? "");
        if (!cid) return;
        seeded[cid] = Array.isArray(c?.tags) ? c.tags : [];
      });
      return seeded;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConnectionsIdsKey, connectionTagSnapshotsKey, selectedConnections, isProcessing, pendingChangesQueue.length, pendingApplyTags.size, pendingRemoveTags.size]);

  // Processing queue for API calls
  useEffect(() => {
    const processQueue = async () => {
      if (isProcessing || pendingChangesQueue.length === 0) return;

      setIsProcessing(true);
      const currentBatch = pendingChangesQueue[0];

      try {
        await executeBatchChanges(
          currentBatch.tagsToApply,
          currentBatch.tagsToRemove
        );
      } catch (error) {
        console.error("Error processing queue item:", error);
      } finally {
        setPendingChangesQueue((prevQueue) => prevQueue.slice(1));
        setIsProcessing(false);
      }
    };

    processQueue();
  }, [isProcessing, pendingChangesQueue, executeBatchChanges]);

  // (union helper used; intersection kept for potential future use)

  const allTagsSorted = useMemo(() => {
    // Merge tags from store and filter options (same approach as ApplyRemoveTagForm)
    const fromFilters: any[] = connectionFilterOptions?.tagFilterOptions || [];
    const fromStore: any[] = allTagsStore || [];
    // Merge unique by _id
    const map = new Map<string, any>();
    [...fromStore, ...fromFilters].forEach((tag: any) => {
      if (tag && tag._id && !map.has(tag._id)) map.set(tag._id, tag);
    });
    let merged: any[] = Array.from(map.values());
    
    // Apply same visibility rule as TagTable: if not workspaceOwner, show only my tags
    if (!workspaceOwner) {
      merged = merged.filter((tag: any) => {
        // Show all public tags to all users, and private tags only to users who have access
        if (tag?.private === false) {
          // Public tags are visible to all users
          return true;
        } else {
          // Private tags are only visible to users who have access
          return tag?.users && tag?.users.some((tagUser: any) => tagUser?._id === user?._id);
        }
      });
    }
    
    const list = merged.filter((t: any) => !t.isForbidden);
    return list.sort((a: any, b: any) =>
      (a?.title || "").localeCompare(b?.title || "")
    );
  }, [allTagsStore, connectionFilterOptions, workspaceOwner, user?._id]);

  const filteredAllTags = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allTagsSorted;
    return allTagsSorted.filter((t: any) =>
      (t?.title || "").toLowerCase().includes(q)
    );
  }, [allTagsSorted, search]);

  // Tags applied to some but not all selected connections (indeterminate / "individual" tab)
  const appliedTags = useMemo(() => {
    if (selectedConnections.length === 0) return [];
    return allTagsSorted.filter((tag: any) =>
      localPartialIds.has(tagIdKey(tag))
    );
  }, [allTagsSorted, selectedConnections.length, localPartialIds]);

  // Tags applied to ALL selected connections (intersection) - with optimistic updates
  const appliedToAllTags = useMemo(() => {
    if (selectedConnections.length === 0) return [];

    // Start with real intersection (current state)
    const realIntersection = allTagsSorted.filter((tag: any) => {
      const tid = tagIdKey(tag);
      return selectedConnections.every((conn: any) => {
        const connTags = (conn.tags || []).filter((t: any) => !t.isForbidden);
        return connTags.some((t: any) => tagIdKey(t) === tid);
      });
    });

    // Apply optimistic updates for better UX
    return realIntersection.filter((tag: any) => {
      // Keep tags that are still checked (optimistically applied)
      return localAppliedIds.has(tagIdKey(tag));
    });
  }, [allTagsSorted, selectedConnections, localAppliedIds]);

  /** Enforce: one pipeline (kanban) tag checked at a time. */
  const checkedPipelineTagByKanbanId = useMemo(() => {
    const map = new Map<string, string>();
    allTagsSorted.forEach((tag: any) => {
      if (tag?.forKanban !== true || !tag?.kanban) return;
      const tid = tagIdKey(tag);
      if (!localAppliedIds.has(tid) && !localPartialIds.has(tid)) return;
      const kid = String(tag.kanban);
      // Keep first encountered checked/partial tag for pipeline.
      if (!map.has(kid)) map.set(kid, tid);
    });
    return map;
  }, [allTagsSorted, localAppliedIds, localPartialIds]);

  const filteredAppliedTags = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return appliedTags;
    return appliedTags.filter((t: any) =>
      (t?.title || "").toLowerCase().includes(q)
    );
  }, [appliedTags, search]);

  const filteredAppliedToAllTags = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return appliedToAllTags;
    return appliedToAllTags.filter((t: any) =>
      (t?.title || "").toLowerCase().includes(q)
    );
  }, [appliedToAllTags, search]);

  const tagExists = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return allTagsSorted.some((t: any) => (t?.title || "").toLowerCase() === q);
  }, [search, allTagsSorted]);

  const canCreateTag = useMemo(() => {
    const q = search.trim();
    return q.length > 0 && q.length <= 30 && !tagExists;
  }, [search, tagExists]);

  const listToRender =
    activeTab === "all"
      ? filteredAllTags
      : activeTab === "mutual"
      ? filteredAppliedToAllTags
      : filteredAppliedTags;

  // Auto-focus search input when modal opens
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Reset focus when items change
  useEffect(() => {
    setFocusedIndex(-1);
  }, [listToRender?.length, canCreateTag]);

  // Scroll focused item into view
  useEffect(() => {
    if (focusedIndex === -1) return;

    if (focusedIndex === 0 && canCreateTag) {
      createButtonRef.current?.scrollIntoView({ block: "nearest" });
    } else {
      const idx = canCreateTag ? focusedIndex - 1 : focusedIndex;
      tagItemRefs.current[idx]?.scrollIntoView({ block: "nearest" });
    }
  }, [focusedIndex, canCreateTag]);

  const onToggleTag = (tag: any, checked: boolean) => {
    const tid = tagIdKey(tag);
    if (busyTagIdsRef.current.has(tid)) return;

    markTagsBusy([tid]);

    setLocalAppliedIds((prev) => {
      const next = new Set(Array.from(prev).map(String));
      if (checked) next.add(tid);
      else next.delete(tid);
      return next;
    });
    // Apply from partial or remove: no longer indeterminate
    setLocalPartialIds((prev) => {
      const next = new Set(Array.from(prev).map(String));
      next.delete(tid);
      return next;
    });

    // Optimistic Kanban UI: if a Pipeline tag is unchecked, hide selected cards immediately.
    if (tag?.forKanban === true && tag?.kanban && checked === false) {
      window.dispatchEvent(
        new CustomEvent("ld:kanban-optimistic-remove", {
          detail: {
            kanbanId: String(tag.kanban),
            connectionIds: selectedConnectionsIds || [],
            selectedAllConnections: selectedAllConnections === true,
          },
        })
      );
    }

    // Add change to appropriate pending buffer
    if (checked) {
      // Tag is checked - add to apply buffer, remove from remove buffer
      setPendingApplyTags((prev) => {
        const next = new Set(Array.from(prev).map(String));
        next.add(tid);
        return next;
      });
      setPendingRemoveTags((prev) => {
        const next = new Set(Array.from(prev).map(String));
        next.delete(tid);
        return next;
      });
    } else {
      // Tag is unchecked - add to remove buffer, remove from apply buffer
      setPendingRemoveTags((prev) => {
        const next = new Set(Array.from(prev).map(String));
        next.add(tid);
        return next;
      });
      setPendingApplyTags((prev) => {
        const next = new Set(Array.from(prev).map(String));
        next.delete(tid);
        return next;
      });
    }

    // Schedule batch execution
    setTimeout(() => {
      scheduleBatchExecution();
    }, 0);
  };

  useEffect(() => {
    return () => {
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
        batchTimeoutRef.current = null;
      }

      // Execute any remaining pending changes before unmounting
      const remainingApplyTags = Array.from(pendingApplyTagsRef.current);
      const remainingRemoveTags = Array.from(pendingRemoveTagsRef.current);

      if (remainingApplyTags.length > 0 || remainingRemoveTags.length > 0) {
        executeBatchChangesRef.current(remainingApplyTags, remainingRemoveTags);
      }
    };
  }, []);

  const [confirmUntagAll, setConfirmUntagAll] = useState(false);

  const executeRemoveAll = () => {
    if (selectedConnections.length === 0) return;

    // Get all unique tag IDs that are applied to any of the selected connections
    const allAppliedTagIds = new Set<string>();

    selectedConnections.forEach((conn: any) => {
      const connTags = (conn.tags || []).filter((t: any) => !t.isForbidden);
      connTags.forEach((tag: any) => {
        allAppliedTagIds.add(tagIdKey(tag));
      });
    });

    if (allAppliedTagIds.size === 0) return;

    const idsToRemove = Array.from(allAppliedTagIds).filter(
      (tagId) => !busyTagIdsRef.current.has(String(tagId))
    );
    if (idsToRemove.length === 0) return;

    markTagsBusy(idsToRemove);

    // Add all tags to pendingRemoveTags buffer
    setPendingRemoveTags((prev) => {
      const next = new Set(Array.from(prev).map(String));
      idsToRemove.forEach((tagId) => {
        next.add(String(tagId));
      });
      return next;
    });

    // Clear any pending apply tags for these tags
    setPendingApplyTags((prev) => {
      const next = new Set(Array.from(prev).map(String));
      idsToRemove.forEach((tagId) => {
        next.delete(String(tagId));
      });
      return next;
    });

    // Optimistically remove these tags from local state
    setLocalAppliedIds((prev) => {
      const next = new Set(Array.from(prev).map(String));
      idsToRemove.forEach((tagId) => next.delete(String(tagId)));
      return next;
    });
    setLocalPartialIds((prev) => {
      const next = new Set(Array.from(prev).map(String));
      idsToRemove.forEach((tagId) => next.delete(String(tagId)));
      return next;
    });

    // Schedule batch execution
    setTimeout(() => {
      scheduleBatchExecution();
    }, 0);
  };

  const onRemoveAll = () => {
    if (selectedConnections.length === 0) return;
    const hasAppliedTags = selectedConnections.some((conn: any) => {
      const connTags = (conn.tags || []).filter((t: any) => !t.isForbidden);
      return connTags.length > 0;
    });
    if (!hasAppliedTags) return;
    setConfirmUntagAll(true);
  };

  const onCreateTag = async () => {
    if (!canCreateTag) return;
    const payload = {
      title: search.trim(),
      color: newTagColor || styles.colors.primary600,
      private: newTagPrivate,
    };
    // Avoid immediate refresh to keep UI snappy; we'll optimistically update the store
    setIsCreating(true);
    const result = await createTagHandler(payload, false, false);
    if (result && (result as any)._id) {
      const newId = String((result as any)._id);
      if (busyTagIdsRef.current.has(newId)) {
        setIsCreating(false);
        return;
      }
      markTagsBusy([newId]);
      // Add the new tag to pendingApplyTags buffer
      setPendingApplyTags((prev) => {
        const next = new Set(Array.from(prev).map(String));
        next.add(newId);
        return next;
      });

      // Remove from pendingRemoveTags if it was there
      setPendingRemoveTags((prev) => {
        const next = new Set(Array.from(prev).map(String));
        next.delete(newId);
        return next;
      });

      // Update local state optimistically
      setLocalAppliedIds((prev) => {
        const next = new Set(Array.from(prev).map(String));
        next.add(newId);
        return next;
      });

      // Schedule batch execution
      setTimeout(() => {
        scheduleBatchExecution();
      }, 0);

      setSearch("");
      setNewTagColor(styles.colors.primary600);
      setNewTagPrivate(true);
    }
    setIsCreating(false);
  };

  // Keyboard navigation handler
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const total = listToRender.length + (canCreateTag ? 1 : 0);
    if (total === 0) return;

    switch (e.key) {
      case "Escape":
        e.preventDefault();
        e.stopPropagation();
        // Close modal - this will be handled by CustomModal
        break;
      case "ArrowDown":
        e.preventDefault();
        e.stopPropagation();
        setFocusedIndex((prev) => (prev < 0 ? 0 : (prev + 1) % total));
        break;
      case "ArrowUp":
        e.preventDefault();
        e.stopPropagation();
        setFocusedIndex((prev) =>
          prev < 0 ? total - 1 : (prev - 1 + total) % total
        );
        break;
      case "Enter":
        e.preventDefault();
        e.stopPropagation();
        if (focusedIndex >= 0) {
          const isCreate = canCreateTag && focusedIndex === 0;
          if (isCreate) {
            onCreateTag();
          } else {
            const idx = canCreateTag ? focusedIndex - 1 : focusedIndex;
            const tag = listToRender[idx];
            if (tag) {
              const tid = tagIdKey(tag);
              // Fully checked → remove; partial/unchecked → apply
              const shouldApply = !localAppliedIds.has(tid);
              onToggleTag(tag, shouldApply);
            }
          }
        } else if (canCreateTag) {
          // If no focus but can create tag, create it
          onCreateTag();
        }
        break;
    }
  };

  const tabs: ITabProperties[] = [
    {
      slug: "all",
      title: "All tags",
      selected: activeTab === "all",
      tooltip: "All tags",
    },
    {
      slug: "mutual",
      title: "Mutual tags",
      selected: activeTab === "mutual",
      tooltip: "Tags applied to all selected connections",
    },
    {
      slug: "individual",
      title: "Individual tags",
      selected: activeTab === "individual",
      tooltip: "Tags applied to only some of the selected contacts",
    },
  ];

  // Buffer debounce (pending apply/remove) + queue + in-flight API
  const isApiInProgress = isProcessing || pendingChangesQueue.length > 0 || pendingApplyTags.size > 0 || pendingRemoveTags.size > 0;

  useEffect(() => {
    if (!closeOnBackdropClick) return;
    bulkEditTagsBlockBackdropClose = isApiInProgress;
    return () => {
      bulkEditTagsBlockBackdropClose = false;
    };
  }, [closeOnBackdropClick, isApiInProgress]);

  return (
    <CustomModal
      headingText={"Edit tags"}
      closeButton
      preventClose={isApiInProgress}
      closeBlockedMessage="Saving in progress. Please wait."
      bodyPadding={"0 24px 24px 24px;"}
    >
      <Column gap="16px" onKeyDown={handleKeyDown} tabIndex={0}>
        <SearchInput
          type="text"
          placeholder="Search tags..."
          value={search}
          maxLength={30}
          onChange={(e: any) => setSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          ref={searchInputRef}
        />

        {canCreateTag && (
          <CreateWrapper>
            <Row>
              <ColorSelectDropdownFragment
                color={newTagColor}
                onColorChangedHandler={setNewTagColor}
                openCustomColorModalHandler={() => {}}
                label="Background color"
                showFocusStyle={true}
              />
            </Row>
            {workspace?.isBusiness && (
              <Row>
                <PrivateCheckBoxControl
                  type="tag"
                  value={newTagPrivate}
                  onChangeHandler={setNewTagPrivate}
                />
              </Row>
            )}
            <Row>
              <div
                ref={createButtonRef}
                onMouseEnter={() => setFocusedIndex(0)}
                onKeyDown={handleKeyDown}
                tabIndex={0}
                style={{ marginTop: 4 }}
              >
                <Button
                  $bigButton
                  $type="blue"
                  $customWidth
                  $customDisabled={styles.colors.primary400}
                  isLoading={isCreating}
                  loadingText={"Creating..."}
                  onClickHandler={onCreateTag}
                >
                  {"Create tag"}
                </Button>
              </div>
            </Row>
          </CreateWrapper>
        )}

        <TabsView
          tabs={tabs}
          onSwitchTab={(slug: any) => setActiveTab(slug)}
          borderBottom={false}
          showUnderline={true}
          inactiveColor={styles.colors.black300}
          rightContent={
            <RemoveAllAction
              onClick={() => onRemoveAll()}
              disabled={
                confirmUntagAll ||
                selectedConnections.length === 0 ||
                !selectedConnections.some((conn: any) => {
                  const connTags = (conn.tags || []).filter(
                    (t: any) => !t.isForbidden
                  );
                  return connTags.length > 0;
                })
              }
            >
              Untag all
            </RemoveAllAction>
          }
        />

        <TagsList>
          {confirmUntagAll ? (
            <UntagAllConfirm>
              <UntagAllConfirmText>
                Are you sure you want to remove all tags from{" "}
                all selected connections? This action{" "}
                cannot be undone.
              </UntagAllConfirmText>
              <UntagAllConfirmActions>
                <Button
                  $type="cancel"
                  $mediumButton
                  onClickHandler={() => setConfirmUntagAll(false)}
                >
                  Cancel
                </Button>
                <Button
                  $type="red"
                  $mediumButton
                  onClickHandler={() => {
                    setConfirmUntagAll(false);
                    executeRemoveAll();
                  }}
                >
                  Untag all
                </Button>
              </UntagAllConfirmActions>
            </UntagAllConfirm>
          ) : (
            <>
              {listToRender.map((tag: any, index: number) => {
                const tid = tagIdKey(tag);
                const checked = localAppliedIds.has(tid);
                const indeterminate = !checked && localPartialIds.has(tid);
                const kanbanId = tag?.forKanban === true && tag?.kanban ? String(tag.kanban) : null;
                const alreadyCheckedForPipeline =
                  kanbanId != null ? checkedPipelineTagByKanbanId.get(kanbanId) : undefined;
                const pipelineDisabled =
                  kanbanId != null &&
                  alreadyCheckedForPipeline != null &&
                  alreadyCheckedForPipeline !== tid;
                const actionBusy = busyTagIds.has(tid);
                const disabled = pipelineDisabled || actionBusy;
                const itemIndex = canCreateTag ? index + 1 : index;
                // Fully checked → remove; partial/unchecked → apply (fills remaining)
                const shouldApply = !checked;
                const appliedOnCount = indeterminate
                  ? selectedConnections.filter((conn: any) =>
                      (conn.tags || []).some((t: any) => tagIdKey(t) === tid)
                    ).length
                  : 0;
                const selectedCount = selectedConnections.length;
                const partialTooltip =
                  indeterminate && selectedCount > 0
                    ? `Tag applied on ${appliedOnCount} of ${selectedCount} selected connections`
                    : "";
                const checkbox = (
                  <Checkbox
                    checked={checked}
                    indeterminate={indeterminate}
                    checkboxId={tag._id}
                    onClickHandler={(e) => {
                      e.stopPropagation();
                      if (disabled) return;
                      onToggleTag(tag, shouldApply);
                    }}
                    $height="24px"
                    disabled={disabled}
                  />
                );
                return (
                  <TagRowItem
                    key={tag._id}
                    ref={(el) => {
                      tagItemRefs.current[index] = el;
                    }}
                    onMouseEnter={() => setFocusedIndex(itemIndex)}
                    onKeyDown={handleKeyDown}
                    tabIndex={0}
                    onClick={() => {
                      if (disabled) return;
                      onToggleTag(tag, shouldApply);
                    }}
                    style={{
                      background:
                        focusedIndex === itemIndex ? "#f0f3ff" : undefined,
                      cursor: "pointer",
                      opacity: pipelineDisabled ? 0.55 : 1,
                    }}
                  >
                    {partialTooltip ? (
                      <TooltipWrapper
                        tooltipText={partialTooltip}
                        placement="right"
                        light
                      >
                        <span>{checkbox}</span>
                      </TooltipWrapper>
                    ) : (
                      checkbox
                    )}
                    <AppTag
                      tag={tag}
                      tooltipMessage={
                        pipelineDisabled
                          ? "One tag per pipeline is allowed."
                          : actionBusy
                          ? "Updating tag…"
                          : undefined
                      }
                    />
                  </TagRowItem>
                );
              })}
              {listToRender.length === 0 && (
                <EmptyState>
                  <Text $faded>
                    {activeTab === "all" && "No tags to show"}
                    {activeTab === "mutual" &&
                      "No tags applied to all selected connections"}
                    {activeTab === "individual" &&
                      "No tags applied to only some of the selected contacts"}
                  </Text>
                </EmptyState>
              )}
            </>
          )}
        </TagsList>
      </Column>
    </CustomModal>
  );
};

const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #eee;
  outline: none;
  padding: 10px 12px;
  font-size: 14px;
  background: #fafbfc;
  border-radius: 4px;
`;

const CreateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 0 8px 0;
  border-bottom: 1px solid ${styles.colors.black200};
`;

const RemoveAllAction = styled.button<{ disabled?: boolean }>`
  background: none;
  border: none;
  color: ${styles.colors.red600};
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  font-weight: 700;
`;

const TagsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  height: 360px;
  min-height: 360px;
  max-height: 360px;
  overflow-y: auto;
`;

const TagRowItem = styled.div`
  padding: 3px 4px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
`;

const UntagAllConfirm = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
  padding: 16px;
`;

const UntagAllConfirmText = styled.p`
  margin: 0;
  font-family: NunitoSans;
  font-size: 14px;
  line-height: 20px;
  color: ${styles.colors.black400};

  strong {
    font-weight: 700;
    color: ${styles.colors.black600};
  }
`;

const UntagAllConfirmActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
`;

export default BulkEditTagsModal;
